class ApiClient {
  private getBaseUrl(): string {
    if (typeof window !== 'undefined') {
      const envUrl = process.env.NEXT_PUBLIC_API_URL;
      // Se NEXT_PUBLIC_API_URL foi configurado para uma URL externa real (produção), usa ela
      if (envUrl && !envUrl.includes('localhost') && !envUrl.includes('127.0.0.1')) {
        return envUrl;
      }
      // No navegador, usa dinamicamente o mesmo protocolo e hostname/IP acessado pelo usuário:
      // Ex: acessou http://192.168.25.220:3000 -> API em http://192.168.25.220:8001/api
      // Ex: acessou http://localhost:3000 -> API em http://localhost:8001/api
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      
      // Se não houver porta explícita (rodando via NGINX/Proxy no domínio), usa apenas /api
      if (!port || port === '80' || port === '443') {
        return `${protocol}//${hostname}/api`;
      }
      return `${protocol}//${hostname}:8001/api`;
    }

    // SSR (dentro do container Docker)
    return process.env.INTERNAL_API_URL || 'http://backend:8001/api';
  }

  private getHeaders(isPublic: boolean = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (!isPublic && typeof window !== 'undefined') {
      const token = localStorage.getItem('access_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`; console.log('Sending token for', isPublic);
      }
    }
    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}, isPublic: boolean = false): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    const headers: Record<string, string> = { ...(this.getHeaders(isPublic) as Record<string, string>), ...(options.headers as any) };
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      let errorMsg = `Erro na requisição: ${response.statusText}`;
      let errorPayload = null;
      try {
        errorPayload = await response.json();
        errorMsg = errorPayload.error || errorPayload.detail || errorPayload.message || JSON.stringify(errorPayload);
      } catch {}
      const error: any = new Error(errorMsg);
      error.status = response.status;
      error.payload = errorPayload;
      throw error;
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // --- Autenticação & Usuário ---
  async login(credentials: { email: string; password: string }) {
    const data = await this.request<{ access: string; refresh: string }>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }, true);
    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);
    }
    return data;
  }

  async register(data: { studio_name: string; name: string; email: string; password: string; phone?: string }) {
    const res = await this.request<{ message: string; user: any; tokens: { access: string; refresh: string } }>('/auth/register/', {
      method: 'POST',
      body: JSON.stringify(data),
    }, true);
    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', res.tokens.access);
      localStorage.setItem('refresh_token', res.tokens.refresh);
    }
    return res;
  }

  logout() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }

  async getMe() {
    return this.request<any>('/auth/me/');
  }

  // --- Clientes ---
  async getClients() {
    const res = await this.request<any>('/clients/');
    return res.results || res;
  }

  async createClient(data: { name: string; email: string; phone?: string; notes?: string; password?: string }) {
    return this.request<any>('/clients/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClient(id: string, data: Partial<{ name: string; email: string; phone: string; notes: string; password: string }>) {
    return this.request<any>(`/clients/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // --- Galerias ---
  async getGalleries(params?: { client_id?: string }) {
    const query = new URLSearchParams(params || {}).toString();
    const res = await this.request<any>(query ? `/galleries/?${query}` : '/galleries/');
    return res.results || res;
  }

  async getGallery(id: string) {
    return this.request<any>(`/galleries/${id}/`);
  }

  async createGallery(data: {
    client: string;
    title: string;
    package_quantity: number;
    extra_photo_price: number;
    watermark_text?: string;
  }) {
    return this.request<any>('/galleries/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGallery(id: string, data: Partial<{ title: string; package_quantity: number; extra_photo_price: number; watermark_text: string }>) {
    return this.request<any>(`/galleries/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async publishGallery(id: string) {
    return this.request<any>(`/galleries/${id}/publish/`, {
      method: 'POST',
    });
  }

  async deleteGallery(id: string) {
    return this.request<any>(`/galleries/${id}/`, {
      method: 'DELETE',
    });
  }

  async deletePhoto(galleryId: string, photoId: string) {
    return this.request<any>(`/galleries/${galleryId}/photos/${photoId}/delete/`, {
      method: 'POST',
    });
  }

  async deletePhotos(galleryId: string, photoIds: string[]) {
    return this.request<any>(`/galleries/${galleryId}/delete-photos/`, {
      method: 'POST',
      body: JSON.stringify({ photo_ids: photoIds }),
    });
  }

  async requestProofUploadUrls(galleryId: string, files: Array<{ filename: string; extension: string; width: number; height: number; file_size: number }>) {
    return this.request<{ message: string; items: Array<{ photo_id: string; filename: string; proof_key: string; upload_url: string }> }>(
      `/galleries/${galleryId}/upload-proofs-presigned/`,
      {
        method: 'POST',
        body: JSON.stringify({ files }),
      }
    );
  }

  async requestFinalUploadUrls(galleryId: string, files: Array<{ photo_id: string; filename: string; extension: string }>) {
    return this.request<{ message: string; items: Array<{ photo_id: string; filename: string; final_key: string; upload_url: string }> }>(
      `/galleries/${galleryId}/upload-finals-presigned/`,
      {
        method: 'POST',
        body: JSON.stringify({ files }),
      }
    );
  }

  async confirmFinals(galleryId: string) {
    return this.request<any>(`/galleries/${galleryId}/confirm-finals/`, {
      method: 'POST',
    });
  }

  async getGallerySelections(galleryId: string) {
    return this.request<any>(`/galleries/${galleryId}/selections/`);
  }

  // --- Pedidos ---
  async getOrders() {
    const res = await this.request<any>('/orders/');
    return res.results || res;
  }

  async getOrderMetrics(params?: { period?: string; start_date?: string; end_date?: string }) {
    const query = new URLSearchParams(params || {}).toString();
    return this.request<{ total_extra_photos: number; total_revenue: string; orders_count: number }>(
      `/orders/metrics/?${query}`
    );
  }

  // --- Configurações ---
  async getSettings() {
    return this.request<any>('/settings/');
  }

  async updateSettings(data: any) {
    return this.request<any>('/settings/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

    async connectMercadoPago(code: string): Promise<any> {
    return this.request<any>('/settings/mp-connect/', {
      method: 'POST',
      body: JSON.stringify({ code })
    });
  }

  async disconnectMercadoPago(): Promise<any> {
    return this.request<any>('/settings/mp-disconnect/', {
      method: 'POST'
    });
  }

  async uploadLogo(file: File) {
    const formData = new FormData();
    formData.append('logo', file);
    return this.request<{ message: string; logo_url: string; watermark_type: string }>('/settings/upload-logo/', {
      method: 'POST',
      body: formData,
    });
  }

  async removeLogo() {
    return this.request<{ message: string; logo_url: null; watermark_type: string }>('/settings/remove-logo/', {
      method: 'DELETE',
    });
  }

  // --- Rotas Públicas do Cliente (Sem Auth) ---
  async getPublicClientPortal(token: string, password?: string, email?: string) {
    const headers: Record<string, string> = {};
    if (password) headers['X-Gallery-Password'] = password;
    if (email) headers['X-Gallery-Email'] = email;
    return this.request<any>(`/public/clients/${token}/`, { headers }, true);
  }
  async getPublicGallery(token: string, password?: string, email?: string) {
    const headers: Record<string, string> = {};
    if (password) headers['X-Gallery-Password'] = password;
    if (email) headers['X-Gallery-Email'] = email;
    return this.request<any>(`/public/galleries/${token}/`, { headers }, true);
  }

  async toggleSelection(token: string, photoId: string, password?: string, email?: string) {
    const headers: Record<string, string> = {};
    if (password) headers['X-Gallery-Password'] = password;
    if (email) headers['X-Gallery-Email'] = email;
    return this.request<any>(`/public/galleries/${token}/toggle-selection/`, {
      method: 'POST',
      body: JSON.stringify({ photo_id: photoId }),
      headers
    }, true);
  }

  async finalizeSelection(token: string, password?: string, email?: string) {
    const headers: Record<string, string> = {};
    if (password) headers['X-Gallery-Password'] = password;
    if (email) headers['X-Gallery-Email'] = email;
    return this.request<any>(`/public/galleries/${token}/finalize-selection/`, {
      method: 'POST',
      headers
    }, true);
  }

  async getPaymentStatus(paymentId: string) {
    return this.request<any>(`/payments/${paymentId}/status/`, {}, true);
  }

  async simulatePayment(paymentId: string) {
    return this.request<any>(`/payments/${paymentId}/simulate-approval/`, {
      method: 'POST',
    }, true);
  }

  async getDownloadUrls(token: string, password?: string, email?: string) {
    const headers: Record<string, string> = {};
    if (password) headers['X-Gallery-Password'] = password;
    if (email) headers['X-Gallery-Email'] = email;
    return this.request<import('./types').ClientDownloadsResponse>(`/public/galleries/${token}/download/`, { headers }, true);
  }
}

export const api = new ApiClient();
